import React, { useState, useEffect } from 'react';
import { X, Keyboard, Search, Navigation, ArrowUpDown, CornerDownLeft, Command } from 'lucide-react';

/** True when the user is typing in a field — shortcuts should be suppressed. */
const isTypingContext = () => {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

const Kbd = ({ children }) => (
  <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-[11px] font-sans font-semibold text-gray-700 shadow-sm">
    {children}
  </kbd>
);

const SHORTCUTS = [
  {
    group: 'Search & Navigation',
    icon: Search,
    items: [
      { keys: (<><Kbd>⌘</Kbd><span className="text-gray-400">/</span><Kbd>Ctrl</Kbd><Kbd>K</Kbd></>), desc: 'Open global docs search' },
      { keys: (<><Kbd>g</Kbd><span className="text-gray-400 text-xs">then</span><Kbd>d</Kbd></>), desc: 'Jump to any page in this section (breadcrumb dropdown)' },
    ],
  },
  {
    group: 'List navigation',
    icon: Navigation,
    items: [
      { keys: (<><Kbd>↑</Kbd><Kbd>↓</Kbd></>), desc: 'Move highlight up / down' },
      { keys: <Kbd>Enter</Kbd>, desc: 'Select highlighted page' },
      { keys: <Kbd>Esc</Kbd>, desc: 'Close dropdown or modal' },
    ],
  },
  {
    group: 'Meta',
    icon: Command,
    items: [
      { keys: <Kbd>?</Kbd>, desc: 'Show this cheat sheet' },
    ],
  },
];

const KeyboardShortcutsOverlay = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKey = (e) => {
      // Close on Escape
      if (open && e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (open) return;
      if (isTypingContext()) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // '?' shortcut — Shift+/ on US keyboards, or the dedicated key
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      data-testid="shortcuts-overlay"
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="shortcuts-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#1F4E79] to-[#163a5f]">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-9 h-9 bg-white/10 border border-white/20 rounded-lg">
              <Keyboard size={18} className="text-[#ef5b25]" />
            </div>
            <div>
              <p className="text-[10px] font-bold text-[#ffcba6] uppercase tracking-[0.15em]">Cheat Sheet</p>
              <h3 className="text-white font-bold text-base">Keyboard Shortcuts</h3>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-1.5 text-white/70 hover:text-white hover:bg-white/10 rounded-md transition-colors"
            aria-label="Close"
            data-testid="shortcuts-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Groups */}
        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto space-y-6">
          {SHORTCUTS.map((grp) => {
            const Icon = grp.icon;
            return (
              <div key={grp.group}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={14} className="text-[#1F4E79]" />
                  <p className="text-[11px] font-bold text-[#1F4E79] uppercase tracking-[0.15em]">{grp.group}</p>
                </div>
                <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 overflow-hidden">
                  {grp.items.map((item, i) => (
                    <li key={i} className="flex items-center justify-between gap-4 px-4 py-2.5 hover:bg-gray-50 transition-colors">
                      <span className="text-sm text-gray-700">{item.desc}</span>
                      <span className="flex items-center gap-1 flex-shrink-0">{item.keys}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <ArrowUpDown size={12} /> navigate · <CornerDownLeft size={12} /> select · <Kbd>Esc</Kbd> close
          </div>
          <span className="text-[10px] text-gray-400 font-mono">Press <Kbd>?</Kbd> anywhere in the docs</span>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsOverlay;
